<template>
    <span ref="select" v-click-outside="hide"
          :class="{
                'select2 select2-container select2-container--default select2-container--above': true,
                'select2-container--disabled': disabled,
                'select2-container--focus select2-container--open': showed
          }" @click="toggleSelect">
        <span class="selection">
            <span class="select2-selection select2-selection--single" >
                <span class="select2-selection__rendered">
                    <span v-if="canBeEmpty && modelValue.length" class="select2-selection__clear" @click.stop="$emit('update:modelValue', [])">×</span>
                    <slot name="optionContent" v-if="modelValue.length">{{ selectedValue }}</slot>
                    <span name="placeholder" v-else class="select2-selection__placeholder">{{ placeholder }}</span>
                </span>
                    <span class="select2-selection__arrow" role="presentation">
                    <b role="presentation"></b>
                </span>
            </span>
        </span>
        <span class="dropdown-wrapper" aria-hidden="true"></span>
    </span>

    <Teleport to="body" v-if="showed">
        <span
            ref="options"
            @click.stop
            :class="{
                'select2-container select2-container--default': true,
                'select2-container--open': showed,
            }"
            :style="{ 'z-index': zIndex, position: 'absolute' }"
        >
        <span class="select2-dropdown select2-dropdown--above">
            <span class="select2-search select2-search--dropdown">
                <input class="select2-search__field" ref="searchInput" v-model="search" @input="debouncedSearch" :placeholder="translate('alerts.enter_some_characters')">
            </span>
            <span class="select2-results">
                <ul class="select2-results__options" aria-expanded="true" aria-hidden="false" ref="list">
                    <slot name="additionalOptions" :selectValue="selectValue"></slot>
                    <li
                        :class="{
                            'select2-results__option': true,
                            'select2-results__option--highlighted': index === highlightedIndex
                        }"
                        :aria-selected="optionIsSelected(option)"
                        v-for="(option, index) in options"
                        :title="findByPath(option, optionValue)"
                        @mouseover="highlightedIndex = index"
                        @click.stop="selectValue(option)"
                    >
                        {{ findByPath(option, optionValue) }}
                    </li>
                    <li v-if="options.length === 0" class="select2-results__option select2-results__message">{{translate('alerts.no_records_found')}}</li>
                    <li v-if="isFetching" class="select2-results__option" aria-disabled>Loading<span class="dots">.</span></li>
                </ul>
            </span>
            <div v-if="hasCreateButton" class="options-footer">
                <AlphaBackgroundButton color="teal" @click="$emit('click:create')">
                    {{ translate('buttons.create') }}
                </AlphaBackgroundButton>
            </div>
        </span>
        </span>
    </Teleport>
</template>
<script>
import vClickOutside from 'click-outside-vue3'
import { createPopper } from '@popperjs/core';
import AlphaBackgroundButton from "../../button/AlphaBackgroundButton.vue";
import { httpClient } from "../../../../services/HttpClient";
export default {
    name: "MultiAutoComplete",
    components: {
        AlphaBackgroundButton
    },
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        url: {
            type: String,
            required: true
        },
        optionKey: {
            type: String,
            default: 'uuid'
        },
        optionValue: {
            type: String,
            default: 'name'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        queryParams: {
            type: Object,
            default: () => {return {}}
        },
        placeholder: {
            type: String,
            default: () => translate('components.select.default_placeholder')
        },
        hasCreateButton: {
            type: Boolean,
            default: false
        },
        limit: {
            type: Number,
            default: 10
        },
        canBeEmpty: {
            type: Boolean,
            default: true
        }
    },
    emits: ['update:modelValue', 'click:create'],
    data() {
        return {
            search: '',
            showed: false,
            highlightedIndex: null,
            zIndex: 950,
            options: [],
            debounceTimer: null,
            page: 0,
            listScrollHandler: null,
            isFetching: false,
            total: null,
            positionTimer: null,
            popperInstance: null
        }
    },
    methods: {
        optionIsSelected(option) {
            const selectedOptionKeys = this.modelValue.map(option => 
                this.findByPath(option, this.optionKey)
            );

            const optionKey = this.findByPath(option, this.optionKey);

            return selectedOptionKeys.includes(optionKey);
        },
        toggleSelect() {
            if (this.disabled) {
                return;
            }

            this.showed ? this.hide() : this.show();
        },
        async show() {
            if (this.showed) {
                return;
            }

            await this.fetchOptions(this.modelValue);

            this.highlightedIndex = this.options.length > 0 ? 0 : null;
            this.showed = true;

            this.$nextTick(() => {
                if (!this.$refs.select) {
                    this.showed = false;
                    return;
                }

                if (this.$refs?.searchInput) {
                    this.$refs.searchInput.focus();
                }

                addEventListener('keydown', this.handleKeyDown);

                this.createPopper();
                this.addReachEndOnScrollListener();
            });
        },
        addReachEndOnScrollListener() {
            this.listScrollHandler = this.onListScroll.bind(this);
            this.$refs.list.addEventListener('scroll', this.listScrollHandler);
        },
        removeReachEndOnScrollListener() {
            if (!this.$refs.list) {
                return;
            }
            this.$refs.list.removeEventListener('scroll', this.listScrollHandler);
            this.listScrollHandler = null;
        },
        onListScroll(event) {
            const el = event.target;
            const threshold = 10;
            const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

            if (atBottom) {
                this.fetchOptions(this.modelValue);
            }
        },
        hide() {
            if (!this.showed) {
                return;
            }

            this.showed = false;
            this.destroyPopper();
            this.removeReachEndOnScrollListener();
            this.search = '';
            this.highlightedIndex = null;
            removeEventListener('keydown', this.handleKeyDown);
            this.options = [];
            this.isFetching = false;
            this.page = 0;
            this.total = null;
        },
        createPopper() {
            if (this.$refs.select && this.$refs.options) {
                this.popperInstance = createPopper(this.$refs.select, this.$refs.options, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top-start'],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                            },
                        },
                        {
                            name: 'sameWidth',
                            enabled: true,
                            phase: 'beforeWrite',
                            requires: ['computeStyles'],
                            fn: ({ state }) => {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                            effect: ({ state }) => {
                                state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                            },
                        },
                    ],
                });
            }
        },
        destroyPopper() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        handleKeyDown(event) {
            if (event.key === 'ArrowUp') {
                this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'ArrowDown') {
                this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.options.length - 1);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'Enter') {
                if (this.options[this.highlightedIndex] === undefined) {
                    return;
                };
                this.selectValue(this.options[this.highlightedIndex])
            }
        },
        scrollIntoViewIfNeeded() {
            const option = this.$refs.list.children[this.highlightedIndex];
            if (option) {
                option.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth'
                });
            }
        },
        findByPath(object, patch) {
            let parts = patch.split('.');

            if (parts.length === 1) {
                return object[parts[0]];
            }

            return this.findByPath(object[parts[0]], parts.slice(1).join('.'));
        },
        async fetchOptions(alreadySelected) {
            if (this.options.length === this.total || this.isFetching) return;
            this.page++;

            this.isFetching = true;

            let requestBody = {
                limit: this.limit,
                page: this.page,
                ...this.queryParams
            }

            if (this.search.length > 0) {
                requestBody.search = this.search;
            }

            await httpClient.get(this.url, requestBody).then((response) => {
                this.total = response.data.meta.total;
                this.options.push(...response.data.data)
            })
            this.isFetching = false;
        },
        debouncedSearch() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.options = [];
                this.page = 0;
                this.total = null;
                this.fetchOptions(this.modelValue);
            }, 500);
        },
        selectValue(newValue) {
            let newArray = [];
            
            if (this.optionIsSelected(newValue)) {
                newArray = this.modelValue.filter((value) => this.findByPath(value, this.optionKey) !== this.findByPath(newValue, this.optionKey))
            } else {
                newArray = [...this.modelValue, newValue]
            } 

            this.$emit('update:modelValue', newArray)
            this.fetchOptions(newArray)
        }
    },
    computed: {
        selectedValue() {
            return this.modelValue.map((value) => this.findByPath(value, this.optionValue)).join(', ')
        }
    },
    mounted() {
        let modalCount = document.getElementsByClassName('modal-backdrop').length

        if (modalCount > 0) {
            this.zIndex = 1000 + (modalCount * 10) + 7
        }
    },
    unmounted() {
        removeEventListener('keydown', this.handleKeyDown);
        this.destroyPopper();
        this.removeReachEndOnScrollListener();
        if (this.positionTimer) clearTimeout(this.positionTimer);
    },
    watch: {
        options() {
            if (this.positionTimer) clearTimeout(this.positionTimer);
            this.positionTimer = setTimeout(() => {
                this.positionTimer = null;
                if (this.popperInstance) {
                    this.popperInstance.update();
                }
                if (this.options.length > 0) {
                    this.highlightedIndex = 0
                }
            }, 0)
        }
    },
    directives: {
        clickOutside: vClickOutside.directive
    }
}
</script>
<style scoped>
.options-footer {
    border-top: 1px solid #ddd;
    padding: .5rem;
}
.select2 {
    width: 100%;
}
.select2-results__option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>